import React, { useState, useMemo } from 'react';
import { DateHelper } from '@bryntum/schedulerpro';
import { Skeleton } from '@/modern/components/ui/skeleton';
import { Badge } from '@/modern/components/ui/badge';
import { Button } from '@/modern/components/ui/button';
import FilePreview from '@/modern/components/file-preview';
import { useEntityFileContext } from '@/react/components/entityViews/files/context/EntityFileContext';
import InfiniteScroll from '@/modern/components/infinite-scroll';
import { isImageFile } from '@/other/utils/utils';
import { Loader2 } from 'lucide-react';

type FileFilter = 'photos' | 'documents';
type ViewMode = 'grid' | 'list';

export const FileGridView = () => {
  const {
    isFetchingPhotos,
    hasMorePhotos,
    fetchCompanyCamPhotos,
    isFetchingDocuments,
    hasMoreDocuments,
    fetchCompanyCamDocuments,
    companyCamPhotos,
    companyCamDocuments,
    boardFiles,
    totalFilesCount
  } = useEntityFileContext();

  const [activeFilter, setActiveFilter] = useState<FileFilter>('photos');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  const isFetchingFiles = useMemo(() => {
    if (activeFilter === 'photos') return isFetchingPhotos;
    if (activeFilter === 'documents') return isFetchingDocuments;
    return isFetchingPhotos || isFetchingDocuments;
  }, [activeFilter, isFetchingPhotos, isFetchingDocuments]);

  const hasMoreFiles = useMemo(() => {
    if (activeFilter === 'photos') return hasMorePhotos;
    if (activeFilter === 'documents') return hasMoreDocuments;
    return false;
  }, [activeFilter, hasMorePhotos, hasMoreDocuments]);

  const fetchMoreFiles = async () => {
    if (activeFilter === 'photos' && hasMorePhotos) {
      await fetchCompanyCamPhotos();
    } else if (activeFilter === 'documents' && hasMoreDocuments) {
      await fetchCompanyCamDocuments();
    }
  };

  // Separate board files into images and documents
  const boardFileImages = useMemo(() => boardFiles.filter(isImageFile), [boardFiles]);
  const boardFileDocuments = useMemo(() => boardFiles.filter(file => !isImageFile(file)), [boardFiles]);

  // Combine photos: image board files first, then CompanyCam photos
  const allPhotos = useMemo(() => [...boardFileImages, ...companyCamPhotos], [boardFileImages, companyCamPhotos]);

  // Combine documents: non-image board files first, then CompanyCam documents
  const allDocuments = useMemo(() => [...boardFileDocuments, ...companyCamDocuments], [boardFileDocuments, companyCamDocuments]);

  const filteredFiles = useMemo(() => {
    switch (activeFilter) {
      case 'photos':
        return allPhotos;
      case 'documents':
        return allDocuments;
      default:
        return allPhotos;
    }
  }, [activeFilter, allPhotos, allDocuments]);

  const filterCounts = useMemo(() => ({
    photos: totalFilesCount - allDocuments.length,
    documents: allDocuments.length
  }), [totalFilesCount, allDocuments.length]);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        {/* View Mode Toggle */}
        <div className="flex gap-1 border-0 rounded-md p-1">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
            className="h-8 px-3"
          >
            <i className="fa-solid fa-grid-2 mr-2" />
            Grid
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="h-8 px-3"
          >
            <i className="fa-solid fa-list mr-2" />
            List
          </Button>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={activeFilter === 'photos' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter('photos')}
          >
            <i className="fa-solid fa-image mr-2" />
            Photos
            <Badge variant={activeFilter === 'photos' ? 'secondary' : 'muted'} size="xs" className="ml-2 rounded-md">
              {filterCounts.photos}
            </Badge>
          </Button>
          <Button
            variant={activeFilter === 'documents' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter('documents')}
          >
            <i className="fa-solid fa-file mr-2" />
            Documents
            <Badge variant={activeFilter === 'documents' ? 'secondary' : 'muted'} size="xs" className="ml-2 rounded-md">
              {filterCounts.documents}
            </Badge>
          </Button>
        </div>
      </div>

      {isFetchingFiles && filteredFiles.length === 0 && (
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <Skeleton className="w-full h-32 rounded-md" />
              <Skeleton className="w-3/4 h-4 rounded-md" />
            </div>
          ))}
        </div>
      )}

      {!isFetchingFiles && filteredFiles.length === 0 && <div className="text-center py-8 text-muted-foreground">No files found</div>}

      {filteredFiles.length > 0 && (
        <>
          {viewMode === 'grid' ? (
            <div className="columns-xs gap-4 space-y-4">
              <InfiniteScroll hasMore={hasMoreFiles} isLoading={isFetchingFiles} next={fetchMoreFiles} threshold={0.5}>
                {filteredFiles.map((file) => (
                  <div key={file.id}>
                    <a className="space-y-2" href={file.originalImageUrl} target="_blank" rel="noopener noreferrer">
                      <span className="w-full bg-muted rounded-lg border border-border flex items-center justify-center overflow-hidden">
                        <FilePreview
                          file={file}
                          src={file.originalImageUrl || file.thumbnailUrl}
                          className="w-full h-full object-cover"
                          showName={true}
                          fileIconClassName="size-40"
                          fileIconContainerClassName="py-4"
                          openFileOnClick={true}
                        />
                      </span>
                      <span className="space-y-1 hidden">
                        <p className="text-sm font-medium truncate" title={file.name}>
                          {file.name}
                        </p>
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          {file.size && <span>{file.size}</span>}
                          {file.lastUpdatedAt && <span>{DateHelper.format(new Date(file.lastUpdatedAt), 'MM/DD/YYYY')}</span>}
                        </div>
                        {file.isCCImage && <span className="text-xs text-muted-foreground">CompanyCam</span>}
                      </span>
                    </a>
                  </div>
                ))}
                {(isFetchingFiles || hasMoreFiles) && (
                  <div className="flex justify-center py-8 w-full">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}
              </InfiniteScroll>
            </div>
          ) : (
            <div className="space-y-2">
              <InfiniteScroll hasMore={hasMoreFiles} isLoading={isFetchingFiles} next={fetchMoreFiles} threshold={0.5}>
                {filteredFiles.map((file) => (
                  <a
                    key={file.id}
                    href={file.originalImageUrl || file.thumbnailUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors group"
                  >
                    <div className="flex-shrink-0">
                      <img
                        src={file.thumbnailUrl || file.originalImageUrl || "https://www.iconpacks.net/icons/2/free-file-icon-1453-thumb.png"}
                        alt={file.name}
                        className="w-12 h-12 rounded object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "https://www.iconpacks.net/icons/2/free-file-icon-1453-thumb.png";
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors" title={file.name}>
                        {file.name || 'Untitled'}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                        {file.addedBy && (
                          <span className="flex items-center gap-1">
                            <i className="fa-solid fa-user text-[10px]" />
                            {file.addedBy}
                          </span>
                        )}
                        {file.size && file.size !== "N/A" && <span>{file.size}</span>}
                        {file.lastUpdatedAt && (
                          <span>{DateHelper.format(new Date(file.lastUpdatedAt), 'MM/DD/YYYY')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="fa-solid fa-external-link text-muted-foreground" />
                    </div>
                  </a>
                ))}
                {(isFetchingFiles || hasMoreFiles) && (
                  <div className="flex justify-center py-8">
                    {isFetchingFiles && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
                  </div>
                )}
              </InfiniteScroll>
            </div>
          )}
        </>
      )}
    </div>
  );
};
